import {
  reactive,
  Ref,
  ref,
  UnwrapRef,
  onUnmounted,
  watch,
  ComponentInternalInstance,
  getCurrentInstance,
  computed,
  ComputedRef,
} from '@vue/composition-api';
import { RowEventContext } from 'tdesign-vue';
import i18n, { i18n_t } from '@/locales/i18n';
import tips, { MsgError } from '@/renderer/scripts/utils/tips';
import smh, { SMH } from '@/renderer/scripts/smh';
import ipc from '@/renderer/scripts/utils/ipc';
import useSort, { Sort } from '@/renderer/components/SpacePage/compositions/useSort';

type TableData = {
  list: any[];
  selectedRowKeys: Array<Number | String>;
  selectedList: Array<object>;
};
type PageData = {
  currPage: number;
  pageSize: number;
  total: number;
  pageSizeOptions: Array<any>;
};

type Handler = {
  tableData: UnwrapRef<TableData>;
  pageData: UnwrapRef<PageData>;
  loading: Ref<boolean>;
  loadingOnHeader: Ref<boolean>; // 在顶部loading，loading期间list内容不变
  sort: UnwrapRef<Sort>;
  columns: ComputedRef<Array<any>>;
  methods: {
    pullList: () => Promise<void>;
    refresh: () => void;
    silentRefresh: () => void;
    onPageSizeChange: (pageSize: number) => void;
    onCurrentChange: (current: number) => void;
    switchSort: (columnName: string) => void;
    sortChange: (newSort: Sort) => void;
    cancelLink: (params: Array<number>) => void;
  };
};
export default function linkListHandle(isConsole: boolean, linkQueryParams?: SMH.OrgShareListParams): Handler {
  const currComponent: ComponentInternalInstance = getCurrentInstance() as ComponentInternalInstance;
  const vm = currComponent.proxy;
  const store = vm.$store;
  const isMobileStyle = computed(() => store.getters.isMobileStyle);
  const inGroupManage = computed(() => linkQueryParams?.groupId !== undefined && isConsole);
  const generalColumns = [
    {
      colKey: 'row-select',
      type: 'multiple',
      width: 26,
    },
    {
      colKey: 'name',
      title: i18n_t('链接名称'),
      sortType: 'all',
      width: 200,
      // TODO因为透传到popup的disable经常不生效，所以是由content和hideEmptyPopup来强制隐藏浮层
      ellipsis: {
        disable: true,
        content: '',
        hideEmptyPopup: true,
      },
    },
    {
      colKey: 'expireTime',
      title: 'title-expireTime',
      titleDesc: i18n_t('过期时间'),
      width: 250,
      sortType: 'all',
      sorter: true,
    },
    {
      colKey: 'creationTime',
      title: 'title-creationTime',
      titleDesc: i18n_t('创建时间'),
      width: 180,
      sortType: 'all',
      sorter: true,
    },
    {
      colKey: 'shareTraffic',
      title: 'title-shareTraffic',
      titleDesc: i18n_t('访问流量'),
      width: 180,
      sortType: 'all',
      sorter: true,
    },
    {
      colKey: 'downloadCountUsed',
      title: i18n_t('下载 | 预览'),
      width: 180,
      sortType: 'all',
      fixed: 'right',
    },
  ];
  const privateGeneralColumns = [
    {
      colKey: 'row-select',
      type: 'multiple',
      width: 26,
    },
    {
      colKey: 'name',
      title: i18n_t('链接名称'),
      sortType: 'all',
      width: 200,
      // TODO因为透传到popup的disable经常不生效，所以是由content和hideEmptyPopup来强制隐藏浮层
      ellipsis: {
        disable: true,
        content: '',
        hideEmptyPopup: true,
      },
    },
    {
      colKey: 'expireTime',
      title: 'title-expireTime',
      titleDesc: i18n_t('过期时间'),
      width: 250,
      sortType: 'all',
      sorter: true,
    },
    {
      colKey: 'creationTime',
      title: 'title-creationTime',
      titleDesc: i18n_t('创建时间'),
      width: 180,
      sortType: 'all',
      sorter: true,
    },
    {
      colKey: 'downloadCountUsed',
      title: i18n_t('下载 | 预览'),
      width: 180,
      sortType: 'all',
      fixed: 'right',
    },
  ];
  const mobileColumns = [
    {
      colKey: 'row-select',
      type: 'multiple',
      width: 26,
    },
    {
      colKey: 'mobile-name',
      title: i18n_t('链接名称'),
      ellipsis: true,
      width: 180,
    },
    {
      colKey: 'operate',
      title: i18n_t('操作'),
      width: 80,
      align: 'right',
    },
  ];
  const consoleColumns = [
    {
      colKey: 'row-select',
      type: 'multiple',
      width: 26,
    },
    {
      colKey: 'name',
      title: 'title-name',
      titleDesc: i18n_t('链接名称'),
      ellipsis: true,
      sortType: 'all',
      sorter: true,
      width: inGroupManage ? 180 : 200,
    },
    {
      colKey: 'expireTime',
      title: 'title-expireTime',
      titleDesc: i18n_t('过期时间'),
      width: inGroupManage ? 180 : 250,
      sortType: 'all',
      sorter: true,
    },
    {
      colKey: 'nickname',
      title: 'title-nickname',
      titleDesc: i18n_t('创建者'),
      width: inGroupManage ? 180 : 200,
      ellipsis: { disable: true, content: '', hideEmptyPopup: true },
      sortType: 'all',
      sorter: true,
    },
    {
      colKey: 'shareTraffic',
      title: 'title-shareTraffic',
      titleDesc: i18n_t('访问流量'),
      width: 180,
      sortType: 'all',
      sorter: true,
    },
    {
      colKey: 'downloadCountUsed',
      title: i18n_t('下载 | 预览'),
      width: 180,
      sortType: 'all',
      fixed: 'right',
    },
  ];
  const privateConsoleColumns = [
    {
      colKey: 'row-select',
      type: 'multiple',
      width: 26,
    },
    {
      colKey: 'name',
      title: 'title-name',
      titleDesc: i18n_t('链接名称'),
      ellipsis: true,
      sortType: 'all',
      sorter: true,
      width: 200,
    },
    {
      colKey: 'expireTime',
      title: 'title-expireTime',
      titleDesc: i18n_t('过期时间'),
      width: 250,
      sortType: 'all',
      sorter: true,
    },
    {
      colKey: 'nickname',
      title: i18n_t('创建者'),
      width: 200,
      ellipsis: true,
      sortType: 'all',
    },
    {
      colKey: 'downloadCountUsed',
      title: i18n_t('下载 | 预览'),
      width: 180,
      sortType: 'all',
      fixed: 'right',
    },
  ];
  const columns = computed(() => {
    if (isMobileStyle.value) return mobileColumns;
    if (isConsole) {
      if (store.getters.isPrivate) return privateConsoleColumns;
      return consoleColumns;
    }
    if (store.getters.isPrivate) return privateGeneralColumns;
    return generalColumns;
  });
  const tableData = reactive<TableData>({
    list: [],
    selectedRowKeys: [],
    selectedList: [],
  });
  const pageData = reactive<PageData>({
    currPage: 1,
    pageSize: 20,
    total: 0,
    pageSizeOptions: [
      {
        label: i18n_t('每页 {slot1} 条', { slot1: 20 }),
        value: 20,
      },
      {
        label: i18n_t('每页 {slot1} 条', { slot1: 50 }),
        value: 50,
      },
      {
        label: i18n_t('每页 {slot1} 条', { slot1: 100 }),
        value: 100,
      },
      {
        label: i18n_t('每页 {slot1} 条', { slot1: 200 }),
        value: 200,
      },
    ],
  });
  const loading = ref(false);
  const loadingOnHeader = ref(false);
  const {
    innerSort,
    tableSort,
    methods: sortMethods,
  } = useSort('linkList', {
    sortBy: undefined,
    descending: true,
  });
  const methods = {
    async pullList(silentRefresh = false) {
      if (isConsole) {
        methods.pullConsoleList(silentRefresh);
      } else {
        methods.pullDesktopList(silentRefresh);
      }
    },
    clearSelectedAndPullList() {
      tableData.selectedRowKeys = [];
      tableData.selectedList = [];
      methods.pullList();
    },
    async pullDesktopList(silentRefresh = false) {
      const opt = {
        page: pageData.currPage,
        pageSize: pageData.pageSize,
        orderBy: innerSort.value.orderBy || 'creationTime',
        orderByType: innerSort.value.orderByType,
      };
      try {
        loading.value = !silentRefresh;
        loadingOnHeader.value = silentRefresh;
        const { data: listRes } = await smh.userAPI.getSharedLinkList(opt);
        if (loadingOnHeader.value) {
          tips.success(i18n_t('刷新成功'));
        }
        loading.value = false;
        loadingOnHeader.value = false;
        pageData.total = listRes.totalNum;
        tableData.list = listRes.contents;
        // 数据改变的时候，触发total改变
        ipc.emit(ipc.RENDERER_EVENTS_MAP.ON_DATA_LOADED, pageData.total);
      } catch (err) {
        tips.error(err as MsgError);
      }
    },
    async pullConsoleList(silentRefresh = false) {
      const opt = {
        page: pageData.currPage,
        pageSize: pageData.pageSize,
        orderBy: innerSort.value.orderBy || 'expireTime',
        orderByType: innerSort.value.orderByType,
        name: linkQueryParams?.name,
        userFilter: linkQueryParams?.userFilter,
      };
      linkQueryParams?.expireTimeRange?.length &&
        Object.assign(opt, {
          expireTimeStart: linkQueryParams?.expireTimeRange[0],
          expireTimeEnd: linkQueryParams?.expireTimeRange[1],
        });
      const queryGroupList = linkQueryParams?.groupId !== undefined;
      if (queryGroupList) {
        Object.assign(opt, {
          groupId: linkQueryParams?.groupId,
        });
      }

      try {
        loading.value = !silentRefresh;
        loadingOnHeader.value = silentRefresh;
        let result;
        if (queryGroupList) {
          result = await smh.userAPI.getGroupShareList(opt);
        } else {
          result = await smh.userAPI.orgShareList(opt);
        }
        const { data: listRes } = result;
        if (loadingOnHeader.value) {
          tips.success(i18n_t('刷新成功'));
        }
        loading.value = false;
        loadingOnHeader.value = false;
        pageData.total = listRes.totalNum;
        tableData.list = listRes.contents;
        if (queryGroupList) {
          ipc.emit(ipc.RENDERER_EVENTS_MAP.ON_DATA_LOADED, pageData.total);
        }
      } catch (err) {
        tips.error(err as MsgError);
      }
    },
    refresh() {
      tableData.list = [];
      tableData.selectedRowKeys = [];
      tableData.selectedList = [];
      methods.pullList();
    },
    silentRefresh() {
      methods.pullList(true);
    },
    onPageSizeChange(pageSize: number) {
      pageData.pageSize = pageSize;
      methods.pullList();
    },
    onCurrentChange(current: number) {
      pageData.currPage = current;
      methods.pullList();
    },
    switchSort(columnName: string) {
      // 升序→降序→无序→升序......
      methods.sortChange(sortMethods.switchSort(columnName));
    },
    sortChange(newSort: Sort | undefined) {
      // 当取消排序时,newSort为undefined,需恢复默认
      sortMethods.sortChange(newSort);
      methods.pullList();
    },
    handleSelectRows(value: Array<String | Number>, { selectedRowData }: any) {
      tableData.selectedRowKeys = value;
      tableData.selectedList = selectedRowData;
    },
    handleClickRow(context: RowEventContext<any>) {
      const { row, e } = context;
      const { target } = e;
      const { className, parentElement } = target as HTMLElement;
      if (
        target &&
        ((typeof className === 'string' && className.includes('t-checkbox')) ||
          (typeof parentElement?.className === 'string' &&
            parentElement?.className.includes('t-table__cell--fixed-right')))
      )
        return;
      const selectedIndex = tableData.selectedRowKeys.findIndex(id => id === row.id);
      if (selectedIndex > -1) {
        tableData.selectedRowKeys.splice(selectedIndex, 1);
        tableData.selectedList.splice(selectedIndex, 1);
      } else {
        tableData.selectedRowKeys.push(row.id);
        tableData.selectedList.push(row);
      }
    },
    async cancelLink(params: Array<number>) {
      try {
        const res = await smh.userAPI.deleteSharedLinks(null, {
          body: params,
        });
        tips.success(i18n_t('链接删除成功'));
        methods.clearSelectedAndPullList();
      } catch (error) {
        tips.error(error as MsgError);
      }
    },
  };
  methods.pullList();
  watch(
    () => pageData.total,
    val => {
      if (!val) return;
      const newPageNum = Math.ceil(val / pageData.pageSize);
      if (pageData.currPage > newPageNum) {
        pageData.currPage = newPageNum;
        methods.refresh();
      }
    },
  );
  ipc.on(ipc.RENDERER_EVENTS_MAP.LINK_LIST_FRESH, methods.pullList);
  onUnmounted(() => {
    ipc.off(ipc.RENDERER_EVENTS_MAP.LINK_LIST_FRESH, methods.pullList);
  });
  return {
    columns,
    tableData,
    pageData,
    loading,
    loadingOnHeader,
    sort: tableSort,
    methods,
  };
}
